



























import { defineComponent, toRefs, PropType, computed } from '@vue/composition-api';
import AppText from '@berlin-front/common/components/elements/AppText';
import { useContext } from '@berlin-front/common/composables/context';
import useCss from '@berlin-front/common/composables/useCss';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import { bemx, camelCase } from '@berlin-front/common/utils';
import useId from '@berlin-front/common/composables/useId';
import { useButtonSize, BUTTON_SIZE, ButtonSize } from './composable';

/**
 * ボタンに設定されるborder-style
 */
type LineStyle = 'none' | 'hidden' | 'dotted' | 'dashed' | 'solid' | 'double' | 'groove' | 'ridge' | 'inset' | 'outset';

/**
 * ## 汎用ボタンパーツ。
 *
 * v-btnのラッパー。
 * 以下の感じで実装する。
 *
 * - 基本的にflexで広がる仕様。
 * - ボタンのsize（高さ）はとりあえず2種類。住所入力のところと、普通のボタン。
 * - ボタンの種類は、fillとoutlineとグレーボタンの3種類と思われる。
 * - APIから色テーマを取れるようにするらしい？ので、scssではなくカラーコードを当てる感じ。テーマはお客先でカスタムできる感じ。
 * - テーマはコンテキストで与えるようにするため、propsにインターフェイスを持たないで良い。コンテキストについてはあとで仕組み提供する。
 */
export default defineComponent({
  name: 'AppButton',

  components: {
    AppText,
  },

  props: {
    /** 無効化
     * 無効化の状態にしたい場合はtrue
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * 高さ
     * 原則高さはデフォルトで固定だが、例外的に高さを変更するときのみ指定
     */
    height: {
      type: String as PropType<ButtonSize>,
      default: BUTTON_SIZE.DEFAULT,
    },

    /**
     * ロード中
     * ロード中の状態にしたい場合はtrue
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * 枠線
     * アウトラインスタイルにしたい場合はtrue
     */
    outlined: {
      type: Boolean,
      default: false,
    },

    /**
     * アウトラインの形状。
     */
    outlineStyle: {
      type: String as PropType<LineStyle>,
      default: 'solid',
    },

    dataTestid: {
      type: String,
      default: '',
    },
  },

  emits: [
    /**
     * クリックイベント
     * @type {MouseEvent}
     */
    'click',
  ],

  setup(props, context) {
    const id = useId();
    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);
    const primaryPressedColor = computed(() => themeParameter.value.primaryPressedColor);
    const outlinePressedColor = computed(() => themeParameter.value.outlinePressedColor);
    const primaryDisabledColor = computed(() => themeParameter.value.primaryDisabledColor);

    const onClick = (event: MouseEvent) => {
      context.emit('click', event);
    };

    const { height } = toRefs(props);
    const setHeight = useButtonSize(height);

    const className = computed(() =>
      bemx('AppButton', {
        colorClass: camelCase(
          [
            props.loading ? 'loading' : undefined,
            props.disabled ? 'disabled' : undefined,
            props.outlined ? 'outlined' : undefined,
          ]
            .filter((v) => v)
            .join('_'),
        ),
        sizeClass: props.height === 'low' ? 'heightLow' : undefined,
        instance: id,
      }),
    );

    useCss(
      () => `
        .AppButton.AppButton--colorClass-outlined .AppText.AppText--color-normal {
          color: ${primaryColor.value};
        }
        .AppButton.AppButton--colorClass-loading {
          background-color: ${primaryPressedColor.value} !important;
        }
        .AppButton.AppButton--colorClass-disabled.v-btn.v-btn--disabled.v-btn--has-bg {
          background-color: ${primaryDisabledColor.value} !important;
        }
        .AppButton.AppButton--colorClass-loadingOutlined {
          color: ${primaryPressedColor.value} !important;
          background-color: ${outlinePressedColor.value};
        }
        .AppButton.AppButton--colorClass-disabledOutlined.v-btn.v-btn--disabled {
          color: ${primaryDisabledColor.value} !important;
        }
        .AppButton.AppButton--colorClass-loadingOutlined .AppText.AppText--color-normal {
          color: ${primaryPressedColor.value};
        }
        .AppButton.AppButton--colorClass-disabledOutlined .AppText.AppText--color-normal {
          color: ${primaryDisabledColor.value};
        }
        .AppButton.AppButton--instance-${id}.v-btn.v-btn--outlined {
          border-style: ${props.outlineStyle};
        }
      `,
    );

    return {
      className,
      onClick,
      primaryColor,
      setHeight,
    };
  },
});
